import React from "react";
import { Search, Check, UserCheck, UserX, AlertCircle } from "react-feather";
import { notFound } from "../../Constant";

const AVATAR_GRADIENTS = [
  "linear-gradient(135deg,#6366f1,#8b5cf6)",
  "linear-gradient(135deg,#f59e0b,#ef4444)",
  "linear-gradient(135deg,#10b981,#06b6d4)",
  "linear-gradient(135deg,#8b5cf6,#ec4899)",
  "linear-gradient(135deg,#06b6d4,#6366f1)",
];

const getInitials = (name = "") =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";

/**
 * Reusable staff assignment panel used in ManageStaff, ManageLeaveStaff, ManageShiftStaff.
 *
 * Props:
 *   title        string   — panel heading
 *   variant      "with"|"without"  — controls icon/colour
 *   items        array    — staff list [{ id, name, selected, isActive }]
 *   searchValue  string
 *   onSearch     fn(val)
 *   onToggle     fn(staff)
 *   onCheckAll   fn()
 *   allChecked   bool
 *   deactiveLabel string  — label for inactive badge
 */
const StaffAssignPanel = ({
  title,
  variant = "with",
  items = [],
  searchValue,
  onSearch,
  onToggle,
  onCheckAll,
  allChecked,
  deactiveLabel = "Inactive",
}) => {
  const isWith = variant === "with";
  const iconColor = isWith ? "#10b981" : "#f43f5e";
  const iconBg = isWith ? "rgba(16,185,129,0.12)" : "rgba(244,63,94,0.12)";
  const Icon = isWith ? UserCheck : UserX;

  const checkedCount = items.filter((s) => s.selected).length;

  return (
    <div
      style={{
        background: "var(--glass-bg)",
        border: "1px solid var(--glass-border)",
        borderRadius: 16,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Panel header */}
      <div
        style={{
          padding: "14px 18px",
          borderBottom: "1px solid var(--glass-border)",
          background: "rgba(99,102,241,0.02)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 9,
              background: iconBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon size={16} strokeWidth={2} style={{ color: iconColor }} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: "0.9375rem", color: "var(--text-primary)" }}>
              {title}
            </div>
            <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: 1 }}>
              {isWith ? `${items.length} assigned` : `${checkedCount}/${items.length} selected`}
            </div>
          </div>
        </div>

        {/* Select-all button (Only show for 'without' panel) */}
        {!isWith && (
          <button
            type="button"
            onClick={onCheckAll}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "5px 12px",
              borderRadius: 8,
              fontSize: "0.75rem",
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.15s",
              border: `1px solid ${allChecked ? "rgba(99,102,241,0.3)" : "var(--glass-border)"}`,
              background: allChecked ? "rgba(99,102,241,0.1)" : "var(--icon-btn-bg)",
              color: allChecked ? "var(--brand-indigo)" : "var(--text-secondary)",
            }}
          >
            {allChecked && <Check size={12} strokeWidth={3} />}
            {allChecked ? "Deselect All" : "Select All"}
          </button>
        )}
      </div>

      {/* Search */}
      <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--glass-border)" }}>
        <div style={{ position: "relative" }}>
          <Search
            size={13}
            strokeWidth={2}
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--text-muted)",
              pointerEvents: "none",
            }}
          />
          <input
            type="text"
            placeholder="Search staff…"
            value={searchValue}
            onChange={(e) => onSearch(e.target.value)}
            ref={(el) => {
              if (el) el.style.setProperty("padding-left", "30px", "important");
            }}
            style={{
              width: "100%",
              padding: "7px 10px",
              borderRadius: 8,
              border: "1px solid var(--glass-border)",
              background: "var(--icon-btn-bg)",
              color: "var(--text-primary)",
              fontSize: "0.8125rem",
              outline: "none",
            }}
          />
        </div>
      </div>

      {/* Staff list */}
      <div style={{ flex: 1, maxHeight: 340, overflowY: "auto" }}>
        {items.length === 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "36px 20px",
              gap: 8,
            }}
          >
            <AlertCircle size={28} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
            <p style={{ color: "var(--text-muted)", margin: 0, fontSize: "0.8125rem" }}>
              {notFound.dataNotFound}
            </p>
          </div>
        ) : (
          items.map((staff, i) => {
            const isChecked = staff.selected;
            const gradient =
              AVATAR_GRADIENTS[(staff.name?.charCodeAt(0) || i) % AVATAR_GRADIENTS.length];
            return (
              <div
                key={staff.id}
                onClick={() => onToggle(staff)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 16px",
                  cursor: "pointer",
                  borderBottom: i < items.length - 1 ? "1px solid var(--glass-border)" : "none",
                  background: !isWith && isChecked ? "rgba(99,102,241,0.04)" : "transparent",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => {
                  if (isWith || !isChecked) e.currentTarget.style.background = "var(--nav-hover)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background =
                    !isWith && isChecked ? "rgba(99,102,241,0.04)" : "transparent";
                }}
              >
                {/* Custom checkbox (Only show for 'without' panel) */}
                {!isWith && (
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 5,
                      flexShrink: 0,
                      transition: "all 0.15s",
                      background: isChecked ? "var(--brand-indigo)" : "transparent",
                      border: `1.5px solid ${isChecked ? "var(--brand-indigo)" : "var(--glass-border)"}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: isChecked ? "0 2px 8px rgba(99,102,241,0.4)" : "none",
                    }}
                  >
                    {isChecked && <Check size={11} strokeWidth={3} color="#fff" />}
                  </div>
                )}

                {/* Avatar */}
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    flexShrink: 0,
                    background: gradient,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span style={{ fontSize: "0.75rem", fontWeight: 800, color: "#fff" }}>
                    {getInitials(staff.name)}
                  </span>
                </div>

                {/* Name */}
                <span
                  style={{
                    flex: 1,
                    fontSize: "0.875rem",
                    fontWeight: !isWith && isChecked ? 600 : 400,
                    color: "var(--text-primary)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {staff.name}
                </span>

                {/* Inactive badge */}
                {!staff.isActive && (
                  <span
                    style={{
                      padding: "2px 8px",
                      borderRadius: 999,
                      fontSize: "0.625rem",
                      fontWeight: 700,
                      background: "rgba(244,63,94,0.1)",
                      color: "#f43f5e",
                      flexShrink: 0,
                    }}
                  >
                    {deactiveLabel}
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default StaffAssignPanel;
